
import React, { useCallback, useEffect, useState } from "react";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { Add, DeleteOutlineRounded, FilterAltOffRounded } from "@mui/icons-material";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useFormik } from "formik";
import { HeaderButton, Buttons } from "components";
import XGrid from "components/UI/Grid/XGrid";
import Modal from "components/UI/Modal/Modal";
import Filters from "components/Filters";
import { useService } from "components/route/ServiceRoute";
import { usuariosTitles as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/Pages.module.scss";
import { SFHotelEmpleadoGet } from "services/services-mia/SFHotelEmpleadoGet";
import { SFUsuarioHotelGet } from "services/services-mia/SFUsuarioHotelGet";
import { mapEmpleadoList } from "pages/hoteles/utils/mapHotelEmpleados";
import InsertModal from "./Actions/InsertModal";
import { SFConnectionRolGet } from "services/services-mia/SFConnectionRolGet";
import UpdateModal from "./Actions/UpdateModal";
import DeleteModal from "./Actions/DeleteModal";



export default function UsuariosHotel() {
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);
    const { service } = useService();

    const [loading, setLoading] = useState(false);
    const [empleados, setEmpleados] = useState([]);
    const [hoteles, setHoteles] = useState([]);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [roles, setRoles] = useState([]);

    const getRoles = useCallback(async (hotelId) => {
        if (!hotelId) return;
        const result = await SFConnectionRolGet(credentials, { SFHotelId: Number(hotelId) });
        // Si vino error, result[0] tiene code === 0
        if (!Array.isArray(result) || result[0]?.code === 0) return;
        setRoles(result);
        console.log(result)
    }, [credentials]);

    const formik = useFormik({
        initialValues: { hotelId: "" },
        onSubmit: () => getEmpleados(formik.values.hotelId),
    });

    const loadHoteles = useCallback(async () => {
        const result = await SFUsuarioHotelGet(credentials);
        if (Array.isArray(result) && result[0]?.SFHotelId) {
            setHoteles(result);
            return result;
        }
        return [];
    }, [credentials]);

    const getEmpleados = useCallback(async (hotelId) => {
        if (!hotelId) return;
        setEmpleados([]);
        setLoading(true);
        try {
            const result = await SFHotelEmpleadoGet(credentials, { SFHotelId: Number(hotelId) });
            // Validar que result sea un array
            if (Array.isArray(result)) {
                setEmpleados(mapEmpleadoList(result));
            } else {
                console.error('❌ getEmpleados devolvió respuesta inválida:', result);
                setEmpleados([]);
            }
        } catch (error) {
            console.error('❌ Error en getEmpleados:', error);
            setEmpleados([]);
        } finally {
            setLoading(false);
        }
    }, [credentials]);

    useEffect(() => {
        (async () => {
            const result = await loadHoteles();
            const firstHotelId = result[0]?.SFHotelId;
            if (firstHotelId) {
                formik.setFieldValue("hotelId", String(firstHotelId));
                getEmpleados(firstHotelId);
            }
        })();
    }, []);

    useEffect(() => {
        if (formik.values.hotelId) {
            getEmpleados(formik.values.hotelId);
            getRoles(formik.values.hotelId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.hotelId]);

    useEffect(() => {
        setRoute({ wordKey: "Usuarios" });
    }, [service?.servicioNombre, setRoute]);

    const resetFilters = () => {
        const defaultHotelId = hoteles[0]?.SFHotelId ? String(hoteles[0].SFHotelId) : "";
        formik.resetForm({ values: { hotelId: defaultHotelId } });
    };

    const closeModal = () => {
        setModalDetails((prev) => ({ ...prev, isOpen: false }));
        setTimeout(() => setRowSelected({ status: false, row: {} }), 300);
    };

    const handleClick = ({ procede, title }) => {
        setModalDetails({ procede, title, isOpen: true });
    };

    const hotelOptions = [
        { SFHotelId: "", SFHotelNombre: "Selecciona un hotel" },
        ...hoteles
    ];

    const isEmpleadoOutOfReach = rowSelected.row.id && !roles?.some(r => r.rolCod === rowSelected.row.rolCod);

    return (
        <main className={styles.main}>
            <Modal
                open={modalDetails.isOpen}
                title={modalDetails.title}
                onClose={closeModal}
            >
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        hotelId={formik.values.hotelId}
                        updateList={() => getEmpleados(formik.values.hotelId)}
                        closeModal={closeModal}
                        roles={roles}
                    />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        empleado={rowSelected.row}
                        roles={roles}
                        updateList={() => getEmpleados(formik.values.hotelId)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        empleado={rowSelected.row}
                        updateList={() => getEmpleados(formik.values.hotelId)}
                        closeModal={closeModal}
                        setterRowSelected={setRowSelected}
                    />
                )}
            </Modal>

            <div className={styles["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center", }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "select",
                                name: "hotelId",
                                label: "Hotel",
                                disabled: false,
                                options: hotelOptions,
                                firstOptionKey: "SFHotelId",
                                firstOptionName: "SFHotelNombre",
                                firstOptionValue: "SFHotelId",
                                all: false,
                            },
                        ]}
                        disabled={!formik.values.hotelId || !!rowSelected.row.id}
                    />
                    <HeaderButton text="Limpiar filtros" onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text="Actualizar"
                        onClick={() => getEmpleados(formik.values.hotelId)}
                        disabled={!formik.values.hotelId}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text="Nuevo empleado"
                        handleClick={handleClick}

                        data={{ title: "Nuevo empleado", procede: ACTIONS.INSERT }}
                        disabled={!!rowSelected.row.id || roles.length === 0}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text="Modificar Rol"
                        handleClick={handleClick}
                        data={{ title: "Modificar rol", procede: ACTIONS.UPDATE }}
                        disabled={!rowSelected.row.id || isEmpleadoOutOfReach || roles.length === 1}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text="Eliminar empleado"
                        handleClick={handleClick}
                        data={{ title: "Eliminar empleado", procede: ACTIONS.DELETE }}
                        disabled={!rowSelected.row.id || isEmpleadoOutOfReach}
                    >
                        <DeleteOutlineRounded />
                    </HeaderButton>
                </Buttons>
            </div>

            <div className={styles.container}>
                <XGrid
                    rows={empleados}
                    titles={titles}
                    loading={loading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}